import { distributionPartners, type DistributionPartner } from '@/mocks/distributionPartners';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

function PartnerLogo({ partner }: { partner: DistributionPartner }) {
  if (partner.variant === 'badge') {
    return (
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-md bg-cream-300 text-navy-500 text-lg md:text-xl font-bold">
          {partner.mark}
        </span>
        <span className="text-sm md:text-base font-semibold text-navy-400 tracking-wide">
          {partner.name}
        </span>
      </div>
    );
  }

  if (partner.icon) {
    return (
      <div className="flex items-center gap-2.5">
        <span className="w-9 h-9 flex items-center justify-center rounded-md bg-cream-200 text-cream-50">
          <i className={`${partner.icon} text-base`}></i>
        </span>
        <span className="text-sm md:text-base font-bold text-navy-400 tracking-tight">
          {partner.name}
        </span>
      </div>
    );
  }

  if (partner.variant === 'serif') {
    return (
      <div className="text-center">
        <p className="font-serif text-base md:text-lg text-navy-400 tracking-[0.18em] whitespace-nowrap">
          {partner.name}
        </p>
        {partner.sub && (
          <p className="mt-1.5 text-[9px] md:text-[10px] uppercase tracking-[0.28em] text-navy-400/70 whitespace-nowrap">
            {partner.sub}
          </p>
        )}
      </div>
    );
  }

  if (partner.variant === 'bold') {
    return (
      <div className="text-center">
        <p className="text-lg md:text-xl font-extrabold text-navy-500 tracking-tight whitespace-nowrap">
          {partner.name}
        </p>
        {partner.sub && (
          <p className="mt-1.5 text-[9px] md:text-[10px] uppercase tracking-[0.24em] text-navy-400/70 whitespace-nowrap">
            {partner.sub}
          </p>
        )}
      </div>
    );
  }

  return (
    <p className="text-lg md:text-xl font-semibold text-navy-400 tracking-tight whitespace-nowrap">
      {partner.name}
    </p>
  );
}

function MarqueeRow({ items, reverse, duration }: { items: DistributionPartner[]; reverse?: boolean; duration: number }) {
  const loop = [...items, ...items];

  return (
    <div
      className="relative w-full overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)]"
    >
      <div
        className={`flex w-max ${reverse ? 'marquee-track-reverse' : 'marquee-track'}`}
        style={{ animationDuration: `${duration}s` }}
      >
        {loop.map((partner, idx) => (
          <div
            key={`${partner.id}-${idx}`}
            className="shrink-0 mx-1.5 md:mx-2.5 w-[210px] md:w-[260px] h-[92px] md:h-[104px] flex items-center justify-center px-4 bg-white border border-cream-200 rounded-xl transition-colors duration-300 hover:border-gold-300"
          >
            <PartnerLogo partner={partner} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DistributionMarquee() {
  const anim = useScrollAnimation({ threshold: 0.1 });

  const topRow = distributionPartners.slice(0, 5);
  const bottomRow = distributionPartners.slice(5);

  return (
    <section className="w-full py-14 md:py-20 bg-cream-50 overflow-hidden">
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div
          ref={anim.ref}
          className={`max-w-3xl mx-auto text-center animate-on-scroll ${anim.isVisible ? 'animate-visible' : ''}`}
        >
          <p className="text-xs font-medium text-gold-600 uppercase tracking-[0.2em] mb-3">Where Our Books Are Sold</p>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-navy-900">Online Global Book Distribution</h2>
          <p className="mt-3 text-sm md:text-base font-semibold text-gold-600">40,000+ Worldwide Distribution</p>
          <div className="w-16 h-1 bg-gold-400 rounded-full mx-auto mt-4 mb-5"></div>
          <p className="text-sm md:text-base text-navy-700 leading-relaxed">
            Your book is placed in front of readers everywhere through the world&rsquo;s leading online retailers,
            bookstores, and library networks &mdash; in print and digital.
          </p>
        </div>
      </div>

      <div className="mt-10 md:mt-12 space-y-4 md:space-y-5 marquee-wrap">
        <MarqueeRow items={topRow} duration={38} />
        <MarqueeRow items={bottomRow} reverse duration={46} />
      </div>

      <div className="w-full px-4 md:px-6 lg:px-8">
        <p className="mt-9 md:mt-10 text-center text-sm text-navy-500">
          Plus expanded reach through international distributors, wholesalers, and library collections.
        </p>
      </div>
    </section>
  );
}